import React, { useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import { dashboardSalesSummaryAPI, dashboardAPI } from "../../services";
import "./styles/dashboard.css";
import TrendUp from "../../assets/icons/TrendUp";
import TrendDown from "../../assets/icons/TrendDown";
import { useAppSelector } from "../../store/hooks";
const formatNumber = (val) => {
  if (val === null || val === undefined || Number.isNaN(Number(val)))
    return "--";
  try {
    const num = Number(val);
    return num.toLocaleString();
  } catch {
    return val;
  }
};

const SalesSummary = () => {
  const [summary, setSummary] = useState(null);
  const [productBrand, setProductBrand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchedOnce = useRef(false); // prevent double call in StrictMode
  const { isPartsURL, isAccessoriesURL } = useAppSelector((state) => state.domain);

  useEffect(() => {
    if (fetchedOnce.current) return;
    fetchedOnce.current = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [summaryRes, productBrandRes] = await Promise.all([
          dashboardSalesSummaryAPI.getMonthlySalesSummary(),
          dashboardAPI.getTopProductBrand(),
        ]);
        setSummary(summaryRes?.data || summaryRes || {});
        setProductBrand(productBrandRes?.data || productBrandRes || {});
      } catch (err) {
        console.error("Error loading sales summary data:", err);
        setError("Could not load sales summary");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const totalRevenue =
    summary?.total_revenue ??
    summary?.revenue ??
    summary?.total ??
    summary?.totalRevenue;
  const profitMargin = summary?.profit_margin ?? summary?.profitMargin;
  const avgOrderValue =
    summary?.avg_order_value ?? summary?.average_order_value ?? summary?.aov;

  const topProduct =
    productBrand?.top_product ||
    productBrand?.topProduct ||
    summary?.top_product ||
    summary?.topProduct ||
    {};
  const bestBrand =
    productBrand?.top_brand ||
    productBrand?.best_brand ||
    productBrand?.bestBrand ||
    summary?.top_brand ||
    summary?.best_brand ||
    summary?.bestBrand ||
    {};
  const avgOrderProcessing =
    productBrand?.avg_order_processing || summary?.avg_order_processing || {};
  const processingHours = avgOrderProcessing?.hours;
  const changePercent = avgOrderProcessing?.change_percent;
  const trend = avgOrderProcessing?.trend;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Revenue Stats Card */}
      <div className="dashboard-card">
        <h3 className="dashboard-card-title">Sales Summary</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "16px",
          }}
        >
          <div>
            <h4
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)",
                margin: 0,
              }}
            >
              {loading ? "Loading..." : `€ ${formatNumber(totalRevenue)}`}
            </h4>
            <p style={{ fontSize: "12px", color: "#6B7280", margin: 0 }}>
              Total Revenue
            </p>
          </div>
          <div>
            <h4
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#10B981",
                margin: 0,
              }}
            >
              {loading
                ? "Loading..."
                : profitMargin !== undefined
                ? `${profitMargin}%`
                : "--"}
            </h4>
            <p style={{ fontSize: "12px", color: "#6B7280", margin: 0 }}>
              Profit Margin
            </p>
          </div>
          <div>
            <h4
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#111827",
                margin: 0,
              }}
            >
              {loading ? "Loading..." : `€ ${formatNumber(avgOrderValue)}`}
            </h4>
            <p style={{ fontSize: "12px", color: "#6B7280", margin: 0 }}>
              Avg Order Value
            </p>
          </div>
        </div>
        {error && (
          <p style={{ marginTop: "8px", color: "#EF4444", fontSize: "12px" }}>
            {error}
          </p>
        )}
      </div>

      {/* Product & Brand Row */}
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}
      >
        {/* Top Product Card */}
        <div className="dashboard-card">
          <p
            style={{
              fontSize: "12px",
              color: isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)",
              marginBottom: "12px",
              fontWeight: "500",
            }}
          >
            Top Performing Product
          </p>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {topProduct?.image ? (
              <img
                src={topProduct.image}
                alt={topProduct?.name || "Product"}
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "4px",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "4px",
                  backgroundColor: "#F3F4F6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "8px",
                  fontWeight: "600",
                  color: "#6B7280",
                  textAlign: "center",
                  padding: "4px",
                  lineHeight: "1.2",
                }}
              >
                {topProduct?.name
                  ? topProduct.name.slice(0, 12)
                  : "—"}
              </div>
            )}
            <div>
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#111827",
                  margin: 0,
                }}
              >
                {topProduct?.name || "--"}
                {topProduct?.product_suffix && (
                  <span style={{ fontWeight: "600", color: "#111827", marginLeft: "4px" }}>
                    {topProduct.product_suffix}
                  </span>
                )}
              </p>
              <p style={{ fontSize: "11px", color: "#6B7280", margin: 0, marginTop: "2px" }}>
                {[
                  topProduct?.ram,
                  topProduct?.storage,
                  topProduct?.variants?.[0]?.Color,
                  topProduct?.used_mobile,
                ]
                  .filter(Boolean)
                  .join(" | ") || ""}
              </p>
            </div>
          </div>
        </div>

        {/* Best Brand Card */}
        <div className="dashboard-card">
          <p
            style={{
              fontSize: "12px",
              color: isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)",
              marginBottom: "12px",
              fontWeight: "500",
            }}
          >
            Best Selling Brand
          </p>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {bestBrand?.image ? (
              <img
                src={bestBrand.image}
                alt={bestBrand?.name || "Brand"}
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "4px",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  backgroundColor: "#F3F4F6",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "bold",
                  color: "#034EA2",
                }}
              >
                {(bestBrand?.code || bestBrand?.name || "--")
                  .toString()
                  .slice(0, 7)
                  .toUpperCase()}
              </div>
            )}
            <div>
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#111827",
                  margin: 0,
                }}
              >
                {bestBrand?.name || "--"}
              </p>
              <p style={{ fontSize: "12px", color: "#6B7280", margin: 0 }}>
                {bestBrand?.total_sales
                  ? `${bestBrand.total_sales} Sales`
                  : bestBrand?.sales
                  ? `${bestBrand.sales} Sales`
                  : ""}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Processing Time Card */}
      <div className="dashboard-card">
        <p style={{ fontSize: "12px", color: "#6B7280", marginBottom: "8px" }}>
          Avg Order Processing Time
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              backgroundColor: "#F3E8FF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#A855F7",
            }}
          >
            <Clock size={20} />
          </div>
          <div>
            <h4
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#424A53",
                margin: 0,
              }}
            >
              {loading
                ? "Loading..."
                : processingHours !== undefined
                ? `${processingHours} hours`
                : "--"}
            </h4>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "500",
                  color:
                    trend === "up"
                      ? "#EF3352"
                      : trend === "down"
                      ? "#0B7C5B"
                      : "#6B7280",
                  backgroundColor:
                    trend === "up"
                      ? "rgba(239, 51, 82, 0.08)"
                      : trend === "down"
                      ? "rgba(11, 124, 91, 0.1)"
                      : "rgba(107, 114, 128, 0.1)",
                  padding: "2px 8px",
                  borderRadius: "100px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {trend === "up" ? (
                  <TrendUp />
                ) : trend === "down" ? (
                  <TrendDown />
                ) : (
                  <span>-</span>
                )}
                {changePercent !== undefined ? changePercent : "-"}%
              </span>
              <span
                style={{
                  fontSize: "12px",
                  color:
                    trend === "up"
                      ? "#EF3352"
                      : trend === "down"
                      ? "#0B7C5B"
                      : "#6B7280",
                  fontWeight: "400",
                }}
              >
                From last week
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesSummary;
